import * as HttpStatus from 'http-status-codes';

import Configuration from '../configuration';
import { ICampaignStatistics } from '../containers/view/CampaignStatistics';
import ICampaign from './ICampaign';
import IMake from './IMake';
import IModel from './IModel';
import IModelFloorPlan from './IModelFloorPlan';
import { RawCampaign } from './IRawCampaign';
import Utility from './utility';

interface IURLID {
  url: string;
  id: string;
}

export enum CampaignStatus {
  Pending = 1,
  Submitted = 2,
  Scheduled = 3
}

class APIService {
  static getAccessToken = (noBearer: boolean = false): string => {
    let token: string | null = localStorage.getItem('jsforce0_access_token');
    if (token) {
      token = ((!noBearer) ? 'Bearer ' : '') + token;
    } else {
      token = '';
    }
    return token;
  }

  async deleteCampaign(campaignIds: Array<string>): Promise<any> {
    let cookedURLsObj: IURLID[] = [];
    if (campaignIds) {
      for (let i = 0; i < campaignIds.length; i++) {
        const campaignId = campaignIds[i];
        const url = Configuration.getCloudMessagingURL() + "/Campaign/" + campaignId;
        const obj: IURLID = { url: url, id: campaignId };
        cookedURLsObj.push(obj);
      }
    }

    let results = cookedURLsObj.map(async (obj: IURLID) => {
      const url = obj.url;
      try {
        const resp = await this.deleteData(url);
        if (resp.ok) {
          return { error: false, response: 'Success' }
        } else {
          return { error: true, response: await resp.text() }
        }
      }
      catch (error) {
        return { error: true, response: JSON.stringify(error) };
      }
    });

    // Wait until all items are deleted, and return an array of the status of each operation.
    return Promise.all(results)
      .then((values: any) => {
        return values;
      })
  }

  async deleteData(url: string = ''): Promise<Response> {
    return this.fetchData('DELETE', url);
  }

  async putData(url: string = '', data: string | object = {}): Promise<Response> {
    return this.fetchData('PUT', url, data);
  }

  async getData(url: string = ''): Promise<Response> {
    return this.fetchData('GET', url);
  };

  async postData(url: string = '', data: string | object = {}): Promise<Response> {
    return this.fetchData('POST', url, data);
  };

  async fetchData(method: string, url: string = '', data: string | object = {}): Promise<Response> {
    const params: any = {
      method: method,
      cache: 'no-cache',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      headers: {
        'accept': 'text/json',
        'Content-Type': ((typeof data) === 'string') ? 'text/plain' : 'text/json',
        'Authorization': APIService.getAccessToken()
      }
    };

    if (method.toUpperCase() !== "GET") {
      if ((typeof data) === 'string') {
        params.body = data;
      } else {
        params.body = JSON.stringify(data);
      }
    }

    const response = await fetch(url, params);
    return response;
  }

  async submitCampaign(campaignIds: string[]): Promise<any> {
    let cookedURLs: string[] = [];
    if (campaignIds) {
      for (let i = 0; i < campaignIds.length; i++) {
        let campaignId = campaignIds[i];
        let cookedURL = Configuration.getCloudMessagingURL() + "/campaign/" + campaignId + "/_submit";
        cookedURLs.push(cookedURL);
      }
    }

    let results = cookedURLs.map(async (cookedURL: string) => {
      try {
        const resp = (await this.postData(cookedURL));
        if (resp.ok) {
          return { error: false, response: await resp.json() };
        } else {
          return { error: true, response: await resp.text() };
        }
      }
      catch (error) {
        return { error: true, response: JSON.stringify(error) }
      }
    })

    return Promise.all(results)
      .then((values: any[]) => {
        return values;
      })
  }

  async scheduleCampaign(campaignIds: Array<string>, scheduledDateTime: string): Promise<any> {
    let cookedURLs: string[] = [];
    if (campaignIds) {
      for (let i = 0; i < campaignIds.length; i++) {
        let campaignId = campaignIds[i];
        let cookedURL = Configuration.getCloudMessagingURL() + "/campaign/" + campaignId + "/_schedule";
        cookedURLs.push(cookedURL);
      }
    }
    let results = cookedURLs.map(async (cookedURL: string) => {
      const data = { scheduledDateTime: scheduledDateTime };
      try {
        const resp = (await this.postData(cookedURL, data));
        if (resp.ok) {
          return { error: false, response: await resp.json() };
        } else {
          return { error: true, response: await resp.text() };
        }
      }
      catch (error) {
        return { error: true, response: JSON.stringify(error) };
      }
    })

    return await Promise.all(results)
      .then((values: any[]) => {
        return values;
      })
  }

  async unscheduleCampaign(campaignIds: Array<string>): Promise<any> {
    let cookedURLs: string[] = [];
    if (campaignIds) {
      for (let i = 0; i < campaignIds.length; i++) {
        let campaignId = campaignIds[i];
        let cookedURL = Configuration.getCloudMessagingURL() + "/campaign/" + campaignId + "/_unschedule";
        cookedURLs.push(cookedURL);
      }
    }
    let results = cookedURLs.map(async (cookedURL: string) => {
      try {
        const resp = (await this.postData(cookedURL));
        if (resp.ok) {
          return { error: false, response: await resp.json() };
        } else {
          return { error: true, response: await resp.text() };
        }
      }
      catch (error) {
        return { error: true, response: JSON.stringify(error) }
      }
    })

    return Promise.all(results)
      .then((values: any[]) => {
        return values;
      })
  }

  async uploadVINFile(data: string): Promise<any> {
    const url = Configuration.getCloudMessagingURL() + "/claimcheck/campaign/bulkupload";
    return this.postData(url, data)
      .then((response: any) => {
      if (!response.ok) {
        throw new Error(`${response.statusText}: ${response.status}`);
      }
      return response.json();
    })
  }

  async createCampaign(data: ICampaign): Promise<any> {
    delete data.id;
    const url = Configuration.getCloudMessagingURL() + "/Campaign";
    const camp: RawCampaign = new RawCampaign(data);
    const cmp: Object = camp.getObjectWithOnlyDefinedFields();
    return this.postData(url, cmp)
      .then(result => {
        console.log(result.json);
      })
      .catch(error => {
        this.handleError(error);
      })
  }

  async updateCampaign(data: ICampaign): Promise<any> {
    const url = Configuration.getCloudMessagingURL() + "/Campaign/" + data.id;
    const camp: RawCampaign = new RawCampaign(data);

    return this.putData(url, camp)
      .then(json => {
        console.log(json);
      })
      .catch(error => {
        console.log('Unable to update the campaign. ',)
        this.handleError(error);
      })
  }

  async retrieveCampaigns(status: CampaignStatus): Promise<void | Array<ICampaign>> {
    const url = Configuration.getCloudMessagingCampaignSearchURL();
    const data = { statusList: [status] };
    return this.postData(url, data)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          let statusText = HttpStatus.getStatusText(response.status);
          let msg = `Unable to retrieve campaign data.\nUrl: ${response.url}\nStatus: ${statusText + " (" + response.status + ")"}`;
          return Promise.reject(msg);
        }
      })
      .then(entries => {
        return entries.map((entry: any) => {
          const data: any = Utility.createDataFromCampaign(entry);
          return data;
        });
      })
  }

  async retrieveLatitudeAndLongitudeFromAddress(street: string, city: string, state: string, zipCode: string, country: string = 'US'): Promise<void | any> {
    const url = Configuration.getCloudGeoForwardURL();
    const cookedURL = `${url}/${street},${city},${state},${zipCode},${country}.json`;

    return this.getData(cookedURL)
      .then(response => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  async retrieveStatistics(campaignId: string): Promise<void | ICampaignStatistics> {
    const url = Configuration.getCloudMessagingStatisticsURL();
    let cookedURL: string = url.replace('{campaignId}', campaignId);

    return this.getData(cookedURL)
      .then(response => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .catch(error => {
        this.handleError(error);
      });
  }


  async retrieveMakes(): Promise<void | Array<IMake>> {

    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    return fetch(Configuration.getMakeCollectionURL(), myInit)
      .then(response => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then(json => {
        let items: Array<IMake> = new Array<IMake>();

        const itemArray = json.result.items;
        for (var i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }

        // We need to sort the items first based on priority, and then on name.  Min priority is -1.  What is max?
        const maxPriorityItem = items.reduce((prev: IMake, current: IMake): IMake => (prev.Priority > current.Priority) ? prev : current);
        const maxPriority = maxPriorityItem.Priority;
        let priority = maxPriority;
        let sortedItems: Array<IMake> = new Array<IMake>();

        while (priority >= -1) {
          const rank = priority; // Avoid (no-loop-func) issue.
          const itemsWithSamePriority: Array<IMake> = items.filter((make) => make.Priority === rank).sort((a, b) => (a.Name > b.Name) ? 1 : -1);
          if (itemsWithSamePriority && itemsWithSamePriority.length > 0) {
            sortedItems = sortedItems.concat(itemsWithSamePriority);
          }
          priority--;
        }

        return sortedItems;
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  async retrieveModels(makeIdsAndPriorities: Array<{ Id: string, Priority: number }>): Promise<void | Array<IModel>> {
    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    let cookedURLs: Array<{ cookedURL: string, makeId: string, makePriority: number }> = [];
    if (makeIdsAndPriorities) {
      for (let i = 0; i < makeIdsAndPriorities.length; i++) {
        let makeId = makeIdsAndPriorities[i].Id;
        let priority = makeIdsAndPriorities[i].Priority;
        let cookedURL = Configuration.getModelCollectionURL().replace(':makeId', makeId);
        cookedURLs.push({ cookedURL: cookedURL, makeId: makeIdsAndPriorities[i].Id, makePriority: priority });
      }
    }

    let results = cookedURLs.map(async (obj: any) => {
      try {
        const response = await fetch(obj.cookedURL, myInit);
        if (!response.ok) {
          this.handleResponseError(response);
        }

        const makeId = obj.makeId;
        const makePriority = obj.makePriority;

        const json = await response.json();
        let items: IModel[] = [];
        const itemArray: [] = json.result.items.map((itm: any) => {
          itm.makeId = makeId;
          itm.makePriority = makePriority;
          return itm;
        })
        for (var i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }
        return items;
      }
      catch (error) {
        this.handleError(error);
      }
    })

    return Promise.all(results)
      .then((values: any[]) => {
        let unionModels: IModel[] = [];
        for (let i = 0; i < values.length; i++) {
          for (let j = 0; values[i] && j < values[i].length; j++) {
            if (!unionModels.includes(values[i][j])) {
              unionModels.push(values[i][j]);
            }
          }
        }

        if (unionModels.length > 0) {
          const maxMakePriorityModelItem: IModel = unionModels.reduce((prev: IModel, current: IModel) => prev.makePriority > current.makePriority ? prev : current);
          let maxMakePriority = maxMakePriorityModelItem.makePriority;
          console.log(maxMakePriority);

          const maxModelPriorityItem: IModel = unionModels.reduceRight((prev: IModel, current: IModel) => prev.Priority > current.Priority ? prev : current);
          console.log(maxModelPriorityItem);
          const maxModelPriority = maxModelPriorityItem.Priority;
          console.log(maxModelPriority);

          let sortedItems: Array<IModel> = new Array<IModel>();

          while (maxMakePriority >= -1) {
            const rankMake = maxMakePriority; // Avoid (no-loop-func) issue.

            const itemsWithSamePriority: Array<IModel> = unionModels.filter((model) => model.makePriority === rankMake).sort((a: IModel, b: IModel) => {
              let result: number = 0;
              if (a.Priority < b.Priority) {
                result = 1;
              } else if (a.Priority > b.Priority) {
                result = -1;
              } else {
                result = a.Name.toUpperCase().localeCompare(b.Name.toUpperCase());
              }
              return result;
            });
            if (itemsWithSamePriority && itemsWithSamePriority.length > 0) {
              sortedItems = sortedItems.concat(itemsWithSamePriority);
            }
            maxMakePriority--;
          }

          // Test code for viewing the MakePriority and Model Priority as part of the Model Name
          // sortedItems = sortedItems.map((item: IModel) => {
          //   item.Name = item.Name + String(item.makePriority) + String(item.Priority);
          //   return item;
          // })
          return sortedItems;
        }
      })
  }

  // Fetch years based only on the makeId, or if that is undefined, then return all years in the system.
  private async _retrieveYears(makeIds?: string[]): Promise<any[]> {

    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    let cookedURLs: string[] = [];
    if (makeIds) {
      for (let i = 0; i < makeIds.length; i++) {
        let makeId = makeIds[i];
        let cookedURL: string = Configuration.getYearMakeCollectionURL().replace('{makeId}', makeId);
        cookedURLs.push(cookedURL);
      }
    } else {
      cookedURLs.push(Configuration.getYearCollectionURL());
    }

    let results = cookedURLs.map(async (cookedURL: string) => {
      try {
        const response = await fetch(cookedURL, myInit);
        if (!response.ok) {
          this.handleResponseError(response);
        }
        const json = await response.json();
        let items: [] = [];
        const itemArray: [] = json.result.items;
        for (var i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }
        items.sort((a, b) => a - b);
        return items;
      }
      catch (error) {
        this.handleError(error);
      }
    })

    return Promise.all(results)
      .then((values: any[]) => {
        let unionYears: any[] = [];
        for (let i = 0; i < values.length; i++) {
          for (let j = 0; values[i] && j < values[i].length; j++) {
            if (!unionYears.includes(values[i][j])) {
              unionYears.push(values[i][j]);
            }
          }
        }

        unionYears.sort((a, b) => a - b);
        return unionYears;
      })
  }

  async retrieveYears(makeIds: string[], modelIds: string[]): Promise<any[]> {
    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    // If modelIds aren't specified then get years based only on makeIds (if specified else using no makeId at all).
    if (!modelIds || modelIds.length === 0) {
      return this._retrieveYears(makeIds);
    } else {
      let cookedURLs: string[] = [];
      if (makeIds) {
        for (let i = 0; i < makeIds.length; i++) {
          let makeId = makeIds[i];
          for (let j = 0; j < modelIds.length; j++) {
            let modelId = modelIds[j];
            let uncookedURL = Configuration.getYearModelCollectionURL().replace('{makeId}', makeId);
            let cookedURL = uncookedURL.replace('{modelId}', modelId);
            cookedURLs.push(cookedURL);
          }
        }
      }
      let results = cookedURLs.map(async (cookedURL: string) => {
        try {
          const response = await fetch(cookedURL, myInit);
          if (!response.ok) {
            this.handleResponseError(response);
          }
          const json = await response.json();
          let items: [] = [];
          const itemArray: [] = json.result.items;
          for (var i = 0; i < itemArray.length; i++) {
            items.push(itemArray[i]);
          }
          items.sort((a, b) => a - b);

          return items;
        }
        catch (error) {
          this.handleError(error);
        }
      })

      return Promise.all(results)
        .then((values: any[]) => {
          let unionYears: any[] = [];
          // Combine the years for each model into a single sorted array, with no duplicates.
          for (let i = 0; i < values.length; i++) {
            for (let j = 0; values[i] && j < values[i].length; j++) {
              if (!unionYears.includes(values[i][j])) {
                unionYears.push(values[i][j]);
              }
            }
          }
          unionYears.sort((a, b) => a - b);

          return unionYears;
        });
    }
  }

  // Fetch floor plans based only on the makeId and modelId.
  private async _retrieveFloorPlans(makeId?: string, modelId?: string,): Promise<any[]> {
    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    let fullURL: string = makeId ? modelId ?
      Configuration.getFloorplanModelCollectionURL()
        .replace('{makeId', makeId)
        .replace('{modelId', modelId) :
      Configuration.getFloorplanMakeCollectionURL()
        .replace('{makeId}', makeId) :
      Configuration.getFloorplanCollectionURL();
    return fetch(fullURL, myInit)
      .then(response => {
        if (!response.ok) {
          this.handleResponseError(response);
        }
        return response.json();
      })
      .then(json => {
        let items: [] = [];
        const itemArray: [] = json.result.items;
        for (var i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }
        items.sort((a, b) => a - b);
        return items;
      })
      .catch(error => {
        this.handleError(error);
        return [];
      })
  }

  async retrieveFloorPlans(makeIds: string[], modelIds: string[], years: number[]): Promise<any[]> {
    let myHeaders = new Headers();
    myHeaders.append('Authorization', APIService.getAccessToken());
    const myInit = Configuration.getUseTokenWhenFetchingVehicleData() ? {
      method: 'GET',
      headers: myHeaders
    } : undefined;

    let queries: string[] = [];

    if (makeIds) {
      for (let i = 0; i < makeIds.length; i++) {
        let makeId = makeIds[i];
        queries.push('?makes=' + makeId);
      }
    }

    // Build an array of queries, one for each combination of make, model, and year.
    queries = Utility._buildQueryParams(Utility._buildQueryParams(queries, 'model', modelIds), 'year', years);
    let cookedURLs: string[] = queries.map((query) => Configuration.getFloorplanCollectionURL() + query);

    let results = cookedURLs.map(async (cookedURL: string) => {
      try {
        const response = await fetch(cookedURL, myInit);
        if (!response.ok) {
          this.handleResponseError(response);
        }
        const json = await response.json();
        let items: IModelFloorPlan[] = [];
        const itemArray: [] = json.result.items;
        for (var i = 0; i < itemArray.length; i++) {
          items.push(itemArray[i]);
        }
        items.sort((a: IModelFloorPlan, b: IModelFloorPlan) => (a.LciId < b.LciId) ? -1 : 1);
        return items;
      }
      catch (error) {
        this.handleError(error);
      }
    })

    return Promise.all(results)
      .then((values: any[]) => {
        let unionFloorPlans: IModelFloorPlan[] = [];
        for (let i = 0; i < values.length; i++) {
          for (let j = 0; j < values[i].length; j++) {
            if (!unionFloorPlans.includes(values[i][j])) {
              unionFloorPlans.push(values[i][j]);
            }
          }
        }

        unionFloorPlans.sort((a: IModelFloorPlan, b: IModelFloorPlan) =>
          (a.Name.toUpperCase() < b.Name.toUpperCase()) ? -1 : 1);

        return unionFloorPlans;
      })
  }

  handleResponseError(response: any) {
    throw new Error(`${response.statusText}: ${response.status}`);
  }
  handleError(error: any) {
    const errMsg = `${error.name}: ${error.message}`;
    throw new Error(errMsg);
  }
}
export default APIService;

export function processCampaigns(entries: any) {
  return entries.map((entry: any) => {
    const data: any = Utility.createDataFromCampaign(entry);
    return data;
  });
}